/**
 * @description Timesheet tab - recent clock history with daily totals
 *
 * Key features:
 * - List of recent time entries
 * - Daily worked hours totals
 * - Entry type indicators (clock, manual, adjusted)
 * - Weekly summary at top
 */

import React, { useState, useEffect } from 'react';
import { Clock, Calendar } from 'lucide-react';
import { useClockContext } from '../../contexts/ClockContext';
import clockService from '../../services/clockService';
import { formatDuration, formatTime, groupByDate, isPtoEntry } from '../../utils/timeFormatters';

const TimesheetScreen = () => {
  const { companyId } = useClockContext();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async () => {
    try {
      const data = await clockService.getHistory(companyId, 14);
      setEntries(data);
    } catch (err) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const groupedEntries = groupByDate(entries);

  // Calculate weekly total
  const weekTotal = entries.reduce((sum, e) => sum + (e.workedMinutes || 0), 0);

  return (
    <div className="p-4 space-y-4">
      {/* Weekly Summary */}
      <div className="bg-slate-800 rounded-2xl p-5 flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-400">Last 14 Entries</p>
          <p className="text-2xl font-bold text-white">{formatDuration(weekTotal)}</p>
        </div>
        <Calendar className="w-8 h-8 text-primary-400 opacity-50" />
      </div>

      {/* Entries by Day */}
      {entries.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No time entries yet</p>
        </div>
      ) : (
        Object.entries(groupedEntries).map(([date, dayEntries]) => {
          const dayTotal = dayEntries.reduce((sum, e) => sum + (e.workedMinutes || 0), 0);

          return (
            <div key={date}>
              <div className="flex items-center justify-between mb-2 px-1">
                <h3 className="text-sm font-medium text-slate-400">{date}</h3>
                <span className="text-sm text-primary-400 font-medium">{formatDuration(dayTotal)}</span>
              </div>

              <div className="space-y-2">
                {dayEntries.map((entry) => {
                  const pto = isPtoEntry(entry);
                  return (
                  <div key={entry.id} className="bg-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-medium">
                          {pto
                            ? (entry.workedMinutes ? formatDuration(entry.workedMinutes) : 'PTO')
                            : (
                              <>
                                {formatTime(entry.clockInAt)}
                                {entry.clockOutAt ? ` — ${formatTime(entry.clockOutAt)}` : ' — In Progress'}
                              </>
                            )}
                        </p>
                        {entry.breakMinutes > 0 && (
                          <p className="text-xs text-slate-500 mt-1">
                            Break: {entry.breakMinutes} min
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-300">
                          {entry.workedMinutes ? formatDuration(entry.workedMinutes) : '—'}
                        </p>
                        <div className="flex items-center justify-end gap-1.5 mt-0.5">
                          {pto ? (
                            <span className="text-xs text-teal-400">PTO</span>
                          ) : entry.eventId ? (
                            <span className="text-xs text-primary-400">Scheduled</span>
                          ) : (
                            <span className="text-xs text-amber-400">Unscheduled</span>
                          )}
                          {!pto && entry.entryType !== 'clock' && (
                            <span className="text-xs text-slate-500 capitalize">{entry.entryType}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {entry.notes && (
                      <p className="text-xs text-slate-500 mt-2 border-t border-slate-700 pt-2">{entry.notes}</p>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default TimesheetScreen;
